import { Div, Span } from 'client/widget-components/basicComponents';
import Button from 'client/widget-components/components/Button';
import {
    mergeResponsiveStyles,
    ResponsiveStylesProp,
} from 'client/widget-components/responsive-styles/responsiveStylesService';
import { toCSSObject } from 'client/widget-components/responsive-styles/toCSSObject';
import React from 'react';
import styled from 'styled-components';
import { FileIcon, TrashIcon, UploadIcon } from '../icons';
import {
    containerDefaultStyles,
    errorDefaultStyles,
    fileIconDefaultStyles,
    fileInfoContentDefaultStyles,
    fileInfoDefaultStyles,
    fileItemDefaultStyles,
    fileListDefaultStyles,
    fileNameDefaultStyles,
    fileSizeDefaultStyles,
    removeButtonDefaultStyles,
    removeButtonHoverDefaultStyles,
} from './styles';
import { FileUploadProps } from './types';
import { useFilestack } from './useFilestack';
import { useFileUploadState } from './useFileUploadState';

const StyledRemoveButton = styled.button.attrs({
    type: 'button',
})<{ styles?: any; hoverStyles?: any }>(
    ({ styles, hoverStyles }) => [
        toCSSObject(styles),
        hoverStyles && {
            '&:hover': toCSSObject(hoverStyles),
        },
    ]
);

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

FileUpload.Error = ({
    children,
    styles,
    ...rest
}: React.HTMLAttributes<HTMLSpanElement> & {
    styles?: ResponsiveStylesProp;
}) => {
    const mergedStyles = mergeResponsiveStyles([errorDefaultStyles, styles]);
    return (
        <Span styles={mergedStyles} {...rest}>
            {children}
        </Span>
    );
};

function FileUpload({
    onChange,
    apiKey,
    pickerOptions = {},
    buttonText,
    buttonIcon = <UploadIcon size={16} />,
    styles,
    dataGrab,
    dataAuto = 'file-upload',
    error: ErrorFromProps,
    buttonClassName,
}: FileUploadProps) {
    const {
        uploadedFiles,
        error: internalError,
        handleUploadSuccess,
        handleUploadError,
        handleRemove,
    } = useFileUploadState({ onChange });

    const { loading, handlePickAndStore } = useFilestack({
        apiKey,
        pickerOptions,
        onUploadSuccess: handleUploadSuccess,
        onUploadError: handleUploadError,
    });

    const containerStyles = mergeResponsiveStyles([
        containerDefaultStyles,
        styles?.container,
    ]);

    return (
        <Div data-auto={dataAuto} data-grab={dataGrab} styles={containerStyles}>
            <Button
                buttonContent={{ text: buttonText || '' }}
                styles={styles?.button || {}}
                iconComponent={buttonIcon}
                isDisabled={loading}
                dataGrab={`${dataAuto}-button`}
                rootDataAttributes={{ onClick: handlePickAndStore }}
                className={buttonClassName}
            />

            {uploadedFiles.length > 0 && (
                <Div
                    styles={fileListDefaultStyles}
                    data-auto={`${dataAuto}-file-list`}
                >
                    {uploadedFiles.map((file, index) => (
                        <Div
                            key={file.key || file.handle || file.url}
                            styles={fileItemDefaultStyles}
                            data-auto={`${dataAuto}-file-item-${index}`}
                        >
                            <Div styles={fileInfoDefaultStyles}>
                                <Div
                                    styles={fileIconDefaultStyles}
                                    data-auto={`${dataAuto}-file-icon`}
                                >
                                    <FileIcon size={40} />
                                </Div>
                                <Div styles={fileInfoContentDefaultStyles}>
                                    <Div
                                        styles={fileNameDefaultStyles}
                                        data-auto={`${dataAuto}-file-name`}
                                    >
                                        {file.filename}
                                    </Div>
                                    <Div
                                        styles={fileSizeDefaultStyles}
                                        data-auto={`${dataAuto}-file-size`}
                                    >
                                        {formatFileSize(file.size)}
                                    </Div>
                                </Div>
                            </Div>

                            <StyledRemoveButton
                                onClick={() => handleRemove(file.url)}
                                styles={removeButtonDefaultStyles}
                                hoverStyles={removeButtonHoverDefaultStyles}
                                data-auto={`${dataAuto}-remove-button`}
                                aria-label={`Remove ${file.filename}`}
                            >
                                <TrashIcon size={20} />
                            </StyledRemoveButton>
                        </Div>
                    ))}
                </Div>
            )}

            {ErrorFromProps}

            {internalError && (
                <FileUpload.Error>{internalError}</FileUpload.Error>
            )}
        </Div>
    );
}

export default FileUpload;
